import { useEffect } from "react";
import { Knowledge, KnowledgeType } from "../../types";
import classes from "classnames";
import styles from "./index.module.scss";
import {
  Button,
  Markdown,
  Note,
  PageFooter,
  Notification,
} from "@quark/design";
import Code from "../code";
import Coach from "../coach-card";
import Image from "../image";
import JsHtml from "../js-html";

export interface KnowledgesProps {
  className?: string;
  title: string;
  knowledges: Knowledge<KnowledgeType>[];
  step?: number;
  onPrev?: () => void;
  onNext?: () => void;
}

const Knowledges = ({
  className,
  title,
  knowledges = [],
  step = 0,
  onPrev,
  onNext,
}: KnowledgesProps) => {
  useEffect(() => {
    Notification.dispose();
  }, []);

  const renderKnowledge = (
    knowledge: Knowledge<KnowledgeType>,
    index: number
  ) => {
    if (knowledge.type === "MarkdownEditor") {
      const k = knowledge as Knowledge<"MarkdownEditor">;
      return <Markdown children={k.content.text} key={k.content.id} />;
    }
    if (knowledge.type === "Image") {
      const k = knowledge as Knowledge<"Image">;
      return (
        <Image
          src={k.content.src}
          width={k.content.width}
          key={k.content.src}
        />
      );
    }
    if (knowledge.type === "Note") {
      const k = knowledge as Knowledge<"Note">;
      return (
        <Note
          className={styles.block}
          content={k.content.text}
          key={k.content.id}
        />
      );
    }
    if (knowledge.type === "JsHtml") {
      const k = knowledge as Knowledge<"JsHtml">;
      const { htmlCode, jsCode, cssCode, language, runnable } = k.content;
      let code = "";
      if (language === "html") {
        code = htmlCode;
      }
      if (language === "css") {
        code = cssCode;
      }
      if (["javascript", "js", "nodejs"].includes(language)) {
        code = jsCode;
      }
      return (
        <div className={styles.block} key={index + language}>
          <JsHtml
            code={code}
            language={language}
            runnable={runnable}
            dataSource={{
              title: "尝试一下",
              language,
              htmlCode,
              jsCode,
              cssCode,
            }}
          />
        </div>
      );
    }
    if (knowledge.type === "Code") {
      const k = knowledge as Knowledge<"Code">;
      const {
        id,
        shortContent,
        content,
        language,
        runnable,
        title,
        entryFileName,
        timeLimit,
      } = k.content;
      return (
        <div className={styles.block} key={id}>
          <Code
            compId={id}
            entryFilename={entryFileName}
            timeLimit={timeLimit}
            code={shortContent || content}
            language={language}
            runnable={runnable}
            dataSource={{
              title,
              content,
              language,
            }}
          />
        </div>
      );
    }
    if (knowledge.type === "Coach") {
      const k = knowledge as Knowledge<"Coach">;
      const { id, name, description } = k.content;
      return (
        <div className={styles.block} key={id}>
          <Coach challengeId={id} name={name} description={description} />
        </div>
      );
    }
    return null;
  };

  const renderPrev = () => {
    if (step > 1) {
      return (
        <Button variant="outlined" onClick={onPrev}>
          返回
        </Button>
      );
    }
    return <div />;
  };

  const renderNext = () => {
    return (
      <Button variant="filled" onClick={onNext}>
        继续
      </Button>
    );
  };

  return (
    <>
      <div className="container">
        <h2>{title}</h2>
      </div>
      <div className={classes("container", styles.knowledges, className)}>
        {knowledges.map(renderKnowledge)}
      </div>
      <PageFooter className="snapshot-ignore">
        {renderPrev()}
        {renderNext()}
      </PageFooter>
    </>
  );
};

export default Knowledges;
